import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import {
  createAddonCheckout,
  fetchStripeAddons,
  removeAddon,
  stripeAddonsQueryKey
} from '../subscriptions'
import { pricingPlansQueryKey } from './usePricingPlans'
import { getApiErrorMessage } from '@utils'

interface UseStripeAddonsProps {
  slug: string
  enabled?: boolean
}

export const useStripeAddons = ({ slug, enabled = true }: UseStripeAddonsProps) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: stripeAddonsQueryKey(slug),
    queryFn: () => fetchStripeAddons({ slug }),
    enabled
  })

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: stripeAddonsQueryKey(slug) }),
      queryClient.invalidateQueries({ queryKey: pricingPlansQueryKey(slug) }),
      queryClient.invalidateQueries({ queryKey: [slug] })
    ])
  }

  const purchaseMutation = useMutation({
    mutationFn: (addonId: string) => createAddonCheckout({ slug }, { addon_id: addonId }),
    onSuccess: async result => {
      if (result.completed) {
        await invalidate()
        toast.success(t('settings.billing.addons.purchaseSuccess'))
        return
      }
      if (!result.checkout_url) {
        toast.error(t('general.error'))
        return
      }
      window.location.href = result.checkout_url
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, t('general.error')))
    }
  })

  const removeMutation = useMutation({
    mutationFn: (addonId: string) => removeAddon({ slug, addonId }, undefined),
    onSuccess: async () => {
      await invalidate()
      toast.success(t('settings.billing.addons.removeSuccess'))
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, t('general.error')))
    }
  })

  return {
    ...query,
    purchaseAddon: purchaseMutation.mutate,
    removeAddon: (addonId: string, options?: Parameters<typeof removeMutation.mutate>[1]) =>
      removeMutation.mutate(addonId, options),
    isPurchasing: purchaseMutation.isPending,
    isRemoving: removeMutation.isPending
  }
}
